import { useEffect } from "react";
import {
  TextField,
  Grid,
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import CountrySelector from "../../Utils/CountrySelector";
import { useTranslation } from "react-i18next";

const EMAIL_DELIMITER = ";";

/**
 * value: society
 * onChange: (newValue) => void
 * errors: { legal_id?: boolean, type?: boolean }
 */
export default function SocietyForm({ value, onChange, errors = {} }) {
  const { t } = useTranslation();

  const COUNTRY_PHONE_CODE = {
    cr: "506",
    gt: "502",
    sv: "503",
    hn: "504",
    ni: "505",
    pa: "507",
    mx: "52",
    us: "1",
    ca: "1",
    es: "34",
  };

  useEffect(() => {
    if (value.country && COUNTRY_PHONE_CODE[value.country]) {
      onChange({
        ...value,
        phone_country_code: COUNTRY_PHONE_CODE[value.country],
      });
    }
  }, [value.country]); // eslint-disable-line

  const update = (field, val) =>
    onChange({
      ...value,
      [field]: val,
    });

  const contactEmails = (value.contact_email || "")
    .split(EMAIL_DELIMITER)
    .map((email) => email.trim())
    .filter(Boolean);

  return (
    <>
      <TextField
        label={t("Name")}
        variant="standard"
        value={value.name || ""}
        onChange={(e) => update("name", e.target.value)}
        fullWidth
      />

      <TextField
        required
        label={t("Legal ID")}
        variant="standard"
        error={!!errors.legal_id}
        value={value.legal_id || ""}
        onChange={(e) => update("legal_id", e.target.value)}
        fullWidth
      />

      <Box>
        <Typography
          variant="caption"
          color={errors.type ? "error" : "text.secondary"}
        >
          {t("Type")}
        </Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          color="primary"
          value={value.type || ""}
          onChange={(e, newValue) => newValue !== null && update("type", newValue)}
          sx={
            errors.type
              ? { "& .MuiToggleButtonGroup-grouped": { borderColor: "error.main" } }
              : undefined
          }
        >
          <ToggleButton value="S.A">{t("SA")}</ToggleButton>
          <ToggleButton value="S.R.L">{t("SRL")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary">
          {t("Current Status")}
        </Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={value.current_status || ""}
          onChange={(e, newValue) => newValue !== null && update("current_status", newValue)}
        >
          <ToggleButton
            value="active"
            sx={{
              "&.Mui-selected": {
                backgroundColor: "success.main",
                color: "success.contrastText",
                "&:hover": { backgroundColor: "success.dark" },
              },
            }}
          >
            {t("Active")}
          </ToggleButton>
          <ToggleButton
            value="inactive"
            sx={{
              "&.Mui-selected": {
                backgroundColor: "error.main",
                color: "error.contrastText",
                "&:hover": { backgroundColor: "error.dark" },
              },
            }}
          >
            {t("Inactive")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <TextField
        label={t("Maintenance Cost")}
        type="number"
        variant="standard"
        value={value.maintenance_cost ?? ""}
        onChange={(e) =>
          update(
            "maintenance_cost",
            e.target.value === "" ? "" : Number(e.target.value)
          )
        }
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        inputProps={{ min: 0, step: "0.01" }}
        fullWidth
      />

      <TextField
        label={t("Constitution Date")}
        type="date"
        variant="standard"
        InputLabelProps={{ shrink: true }}
        value={
          value.constitution_date && !isNaN(new Date(value.constitution_date))
            ? new Date(value.constitution_date).toISOString().split("T")[0]
            : ""
        }
        onChange={(e) => update("constitution_date", e.target.value)}
        fullWidth
      />

      <CountrySelector
        value={value.country || ""}
        onChange={(country) => update("country", country)}
      />

      <TextField
        label={t("Corporate Address")}
        variant="standard"
        value={value.address || ""}
        onChange={(e) => update("address", e.target.value)}
        fullWidth
      />

      <Autocomplete
        multiple
        freeSolo
        options={[]}
        value={contactEmails}
        onChange={(e, newEmails) =>
          update(
            "contact_email",
            newEmails.map((email) => email.trim()).filter(Boolean).join(EMAIL_DELIMITER)
          )
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={t("Contact Email")}
            variant="standard"
            helperText={t("contact_email_multi_help")}
          />
        )}
      />

      <Grid container spacing={2}>
        <Grid item xs={4}>
          <TextField
            label={t("Country Code")}
            variant="standard"
            value={value.phone_country_code || ""}
            onChange={(e) =>
              update("phone_country_code", e.target.value.replace(/\D/g, ""))
            }
            inputProps={{ inputMode: "numeric" }}
            fullWidth
          />
        </Grid>

        <Grid item xs={8}>
          <TextField
            label={t("WhatsApp Number")}
            variant="standard"
            value={value.phone_number || ""}
            onChange={(e) =>
              update("phone_number", e.target.value.replace(/\D/g, ""))
            }
            helperText={t("whatsapp_format_help")}
            inputProps={{ inputMode: "numeric" }}
            fullWidth
          />
        </Grid>
      </Grid>

      <Box>
        <Typography variant="caption" color="text.secondary">
          {t("Language")}
        </Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          color="primary"
          value={value.language || ""}
          onChange={(e, newValue) => newValue !== null && update("language", newValue)}
        >
          <ToggleButton value="es">{t("Spanish")}</ToggleButton>
          <ToggleButton value="en">{t("English")}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
    </>
  );
}
